import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import './index.scss'
import { activeLayerIds, visibleLayerIds } from '../../../lib/layerVisibility'
import {
  captionFrom, dateUrl, fetchImageryFrames, imageryTemplate, mayHaveImageryYears, pickFor, setPick
} from '../../../lib/timeLayers/imagery'
import { setImageryTemplate } from '../../../lib/timeLayers/frameStore'
import { normalizeCloudfrontUrl } from '../render-layers/map-types'
import { layerMenuTap } from '../../../action/layer-menu-actions'

/**
 * The imagery year pill, for imagery layers with dated archives (NAIP, Sentinel-2), laid
 * out like iOS and Android (ImageryYearView):
 *
 *     2025 ▾                  ✕
 *     NAIP · Aug 2024
 *
 * The year chip opens the layer's years, newest first, the default one marked Latest; with
 * only one there is nothing to pick, so the chip just names the imagery. ✕ turns the layer
 * off as the layers menu does. The caption says when the imagery under the map centre was
 * flown (date.json, 300 ms after the map comes to rest), or asks for a zoom-in below the
 * layer's minimum zoom. Shows whenever such a layer is on, following the top-most one.
 *
 * A pick lives for the page session (not persisted, as on iOS) and reaches the tiles
 * through the frame store, which the 2D and 3D overlays read.
 */

const CAPTION_DELAY_MS = 300

const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
)
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
)
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12l5 5 9-10" />
  </svg>
)

/** Points the layer's tiles at its picked year, or back at `default`. */
const applyPick = layer => {
  const frame = pickFor(layer.id)
  setImageryTemplate(layer.id, frame ? imageryTemplate(normalizeCloudfrontUrl(layer.cloudfrontUrl), frame) : null)
}

const ImageryYear = ({ layers, mapReference, map3d, use3d, allowed, turnOff }) => {
  const [loaded, setLoaded] = useState({})      // layerId -> frames, for the active candidates
  const [, setTick] = useState(0)               // a pick changed
  const [open, setOpen] = useState(false)
  const [caption, setCaption] = useState(null)
  const rootRef = useRef(null)

  const layersKey = layers.map(l => `${l.id}:${l.cloudfrontUrl}`).join('|')

  // ---- Frames -------------------------------------------------------------------------

  useEffect(() => {
    if (!allowed || layers.length === 0) {
      setLoaded({})
      return
    }
    let cancelled = false
    Promise.all(layers.map(layer => fetchImageryFrames(layer).then(frames => [layer, frames]))).then(results => {
      if (cancelled) return
      const next = {}
      results.forEach(([layer, frames]) => {
        if (frames) next[layer.id] = frames
        // A picked year that left frames.json falls back to the default; one whose rev
        // changed redraws with the new rev.
        const picked = pickFor(layer.id)
        if (picked) {
          const current = frames && frames.frames.find(f => f.key === picked.key)
          setPick(layer.id, current && current.key !== frames.defaultKey ? current : null)
        }
        applyPick(layer)
      })
      setLoaded(next)
    })
    return () => { cancelled = true }
  }, [allowed, layersKey])

  // The picker follows the layer drawn on top.
  const layer = layers
    .filter(l => loaded[l.id])
    .reduce((a, b) => (!a || (b.drawOrder || 0) > (a.drawOrder || 0) ? b : a), null)
  const frames = layer ? loaded[layer.id] : null
  const model = (frames && frames.model) || 'NAIP'
  const selectedKey = layer ? ((pickFor(layer.id) || {}).key || frames.defaultKey) : null

  // ---- Caption ------------------------------------------------------------------------

  const target = use3d ? map3d : mapReference
  useEffect(() => {
    setCaption(null)
    if (!layer || !frames || !target) return
    let timer = null
    let controller = null
    let lastQuery = null
    const minZoom = layer.minZoomLevel || frames.minZoom || 0
    const lookup = () => {
      // Google's zoom is the tile zoom; MapLibre's is one less for these 256 px tiles.
      const zoom = use3d ? target.getZoom() + 1 : target.getZoom()
      if (zoom < minZoom) {
        if (controller) controller.abort()
        lastQuery = null
        setCaption(`Zoom in to see ${model} imagery`)
        return
      }
      const centre = target.getCenter()
      const lng = typeof centre.lng === 'function' ? centre.lng() : centre.lng
      const lat = typeof centre.lat === 'function' ? centre.lat() : centre.lat
      const url = dateUrl(layer, lng, lat, selectedKey)
      if (!url || url === lastQuery) return
      lastQuery = url
      if (controller) controller.abort()
      controller = typeof AbortController !== 'undefined' ? new AbortController() : null
      fetch(url, controller ? { signal: controller.signal } : undefined)
        .then(res => (res.ok ? res.json() : null))
        .then(body => {
          if (lastQuery !== url) return
          // A reply for a year no longer showing is dropped.
          if (body && body.t && body.t !== selectedKey) return
          const text = captionFrom(model, body)
          // Outside coverage, or the lookup failed: say nothing rather than something
          // wrong. A failed lookup is retried at the next rest.
          if (!body) lastQuery = null
          setCaption(text)
        })
        .catch(e => {
          if (e && e.name === 'AbortError') return
          lastQuery = null
          setCaption(null)
        })
    }
    const settle = () => {
      clearTimeout(timer)
      timer = setTimeout(lookup, CAPTION_DELAY_MS)
    }
    settle()
    let off
    if (use3d) {
      target.on('moveend', settle)
      off = () => target.off('moveend', settle)
    } else {
      const listener = window.google.maps.event.addListener(target, 'idle', settle)
      off = () => listener.remove()
    }
    return () => {
      clearTimeout(timer)
      if (controller) controller.abort()
      off()
    }
  }, [layer && layer.id, frames, selectedKey, target, use3d])

  // ---- Menu ---------------------------------------------------------------------------

  useEffect(() => {
    if (!open) return
    const onDown = e => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false) }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => { if (!layer) setOpen(false) }, [layer])

  if (!allowed || !layer || !frames) return null

  const years = [...frames.frames].reverse()     // newest first
  const pickable = years.length > 1
  const current = years.find(f => f.key === selectedKey) || years[0]

  const pick = key => {
    setOpen(false)
    const frame = frames.frames.find(f => f.key === key)
    if (!frame || !setPick(layer.id, key === frames.defaultKey ? null : frame)) return
    applyPick(layer)
    setTick(t => t + 1)
  }

  return (
    <div className="imagery-year" ref={rootRef} role="group" aria-label={`${model} imagery`}>
      <div className="imagery-year__row">
        {pickable ? (
          <button
            type="button"
            className="imagery-year__chip"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={`Imagery year ${current.label}`}
            title="Shows imagery from an earlier year"
            onClick={() => setOpen(o => !o)}
          >
            {current.label}
            <ChevronIcon />
          </button>
        ) : (
          <span className="imagery-year__chip is-static">{model}</span>
        )}
        <button type="button" className="imagery-year__close" aria-label={`Turn off ${model}`} title={`Turn off ${model}`}
          onClick={() => turnOff(layer)}>
          <CloseIcon />
        </button>
      </div>
      {caption && <div className="imagery-year__caption">{caption}</div>}
      {open && (
        <div className="imagery-year__menu" role="listbox" aria-label="Imagery year">
          <div className="imagery-year__menu-title">Imagery year</div>
          {years.map(f => (
            <button
              key={f.key}
              type="button"
              role="option"
              aria-selected={f.key === selectedKey}
              className={`imagery-year__option${f.key === selectedKey ? ' is-selected' : ''}`}
              onClick={() => pick(f.key)}
            >
              <span>{f.key === frames.defaultKey ? `${f.label} (Latest)` : f.label}</span>
              {f.key === selectedKey && <CheckIcon />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const mapStateToProps = state => {
  const ids = visibleLayerIds({
    activeIds: activeLayerIds(state.userSettings),
    layersDictionary: state.layersDictionary,
    layersVisible: state.layersVisible
  })
  const layers = ids.map(id => state.layersDictionary?.[id]).filter(l => l && l.enabled !== false && mayHaveImageryYears(l))
  const mapReference = typeof state.mapReference === 'function' ? state.mapReference() : null
  const map3d = state.mapReferenceThreed && typeof state.mapReferenceThreed.getCanvas === 'function'
    ? state.mapReferenceThreed : null
  return {
    layers,
    layersKey: layers.map(l => `${l.id}:${l.cloudfrontUrl}`).join('|'),
    mapReference,
    map3d,
    use3d: !!state.is3dMapselected && !!map3d,
    allowed: state.selectedTab === 'map' && !!mapReference
  }
}

const mapDispatchToProps = dispatch => ({
  // No analytics: the layers menu's off toggle records none either (as iOS and Android).
  turnOff: layer => dispatch(layerMenuTap(layer, { active: true }))
})

// layers is a fresh array on every store change; compare it by its ids and URLs.
export default connect(mapStateToProps, mapDispatchToProps, null, {
  areStatePropsEqual: (next, prev) =>
    next.layersKey === prev.layersKey && next.mapReference === prev.mapReference &&
    next.map3d === prev.map3d && next.use3d === prev.use3d && next.allowed === prev.allowed
})(ImageryYear)
